import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { LeaderAssessmentCreateComponent } from '@pages/leader/assessment';
import {
	Assessment,
	AssessmentStatus,
	LeaderAssessmentService,
} from '@services/leader';
import {
	Group,
	GroupService,
	Organization,
	OrganizationService,
} from '@services/member';
import {
	AsyncDelayService,
	LanguageService,
	LoaderService,
	MainKey,
	StorageService,
} from '@services/public';
import { Select } from '@util';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

const PAGE_PATH = `pages.researcher.dashboard.trackDashboard`;

@Component({
	selector: 'app-leader-dashboard-track',
	templateUrl: './track.component.html',
	styleUrls: ['./track.component.scss'],
})
export class LeaderDashboardTrackComponent implements OnInit, OnDestroy {
	//view all
	modalRef: BsModalRef;
	p: number = 1;
	public ascNumberSort = true;
	finalDate: number;
	table_header_highlight: any = '';
	endDate: boolean = false;
	status: boolean = false;

	// Page subscriptions.
	private _subscriptions: Subscription = new Subscription();

	// Page filters.
	filters = {
		organization: new Select<Organization>(),
		group: new Select<Group>(),
	};

	// Local data storage from API calls.
	groups: Array<Group> = [];
	allAssessments: any = null;

	// Filtered data storage.
	assessments: Array<Assessment> = [];
	assessmentsCompleted: Assessment[];
	assessmentsPending: Assessment[];
	assessmentsActive: Assessment[];

	// Delete assessment selection.
	deleteAss: any;

	// Synchronous loading.
	private _loadAssessmentsKey: symbol = Symbol();

	// Page language.
	page: { [key: string]: string } = {};

	constructor(
		private _asyncDelaySvc: AsyncDelayService,
		private _groupSvc: GroupService,
		private _languageSvc: LanguageService,
		private _organizationSvc: OrganizationService,
		private _storageSvc: StorageService,
		private router: Router,
		private assessmentSvc: LeaderAssessmentService,
		private modalSvc: BsModalService,
		private loaderSvc: LoaderService,
	) {}

	ngOnInit(): void {
		this._languageSvc.get([PAGE_PATH]).then((value) => {
			if (typeof value[PAGE_PATH] === 'object' && value[PAGE_PATH] !== null)
				this.page = value[PAGE_PATH];
		});

		// Set filter events.
		this._subscriptions.add(
			this.filters.organization.onChange.subscribe(
				(organization: Organization) => {
					this._storageSvc.setMainStorage(
						MainKey.SELECTED_ORGANIZATION_UUID,
						organization.uuid,
					);
					this._asyncDelaySvc.asyncDelay(
						this._loadAssessmentsKey,
						this.loadAssessments.bind(this),
					);
				},
			),
		);
		this._subscriptions.add(
			this.filters.group.onChange.subscribe(() => {
				this._asyncDelaySvc.asyncDelay(
					this._loadAssessmentsKey,
					this.loadAssessments.bind(this),
				);
			}),
		);

		// Initialize filters.
		this._groupSvc
			.getGroups(false, (group: Group) => group.role !== null)
			.then(async (value: Array<Group>) => {
				this.groups = value;
				this._subscriptions.add(
					this.filters.organization.onChange.subscribe(
						(organization: Organization) => {
							if (!organization || organization.uuid === null) {
								this.filters.group.options = value;
							} else
								this.filters.group.options = value.filter(
									(group: Group) =>
										group.uuid === null ||
										group.organizationUUID === organization.uuid,
								);
						},
					),
				);
				this.filters.group.loaded = true;
			});
		this._organizationSvc
			.getOrganizations((organization: Organization) => organization.leader)
			.then(async (value: Array<Organization>) => {
				await this.filters.group.loaded;
				this.filters.organization.options = value;
				this.filters.organization.value =
					this.filters.organization.options.find(
						(organization: Organization) =>
							organization.uuid ===
							this._storageSvc.getMainStorage(
								MainKey.SELECTED_ORGANIZATION_UUID,
							),
					) || this.filters.organization.value;
				this.filters.organization.loaded = true;
			});

		this.loadAllAssessments();
	}

	loadAllAssessments(): void {
		// Load all assessments.
		this.assessmentSvc
			.getAssessments()
			.pipe(
				map(async (response: any) => {
					if (!!response) {
						await this.filters.group.loaded;
						response.map((assessment) => {
							const group = this.groups.find(
								(group) => group.uuid === assessment.groupUUID,
							);
							assessment.group = group;
						});
						this.allAssessments = response;
						this.allAssessments.sort((a, b) => {
							if (a.status === b.status) return 0;
							if (a.status === AssessmentStatus.INACTIVE) return -1;
							if (a.status === AssessmentStatus.COMPLETED) return 1;
							if (a.status === AssessmentStatus.SCHEDULED) {
								if (b.status === AssessmentStatus.COMPLETED) return -1;
								return 1;
							}
							if (b.status === AssessmentStatus.INACTIVE) return 1;
							return -1;
						});
						this._asyncDelaySvc.asyncDelay(
							this._loadAssessmentsKey,
							this.loadAssessments.bind(this),
						);
					}
				}).bind(this),
			)
			.subscribe();
	}

	diffDates(curr, memory) {
		var date1 = new Date(memory);
		var date2 = new Date(curr);
		//calculate time difference
		var time_difference = date2.getTime() - date1.getTime();

		//calculate days difference by dividing total milliseconds in a day
		var days_difference = time_difference / (1000 * 60 * 60 * 24);
		return days_difference;
	}

	loadAssessments(): void {
		// Skip if groups or assessments aren't loaded yet.
		this.p = 1;
		if (this.filters.group.value === null) return;
		if (this.allAssessments === null) return;

		// Load assessments.
		const loader: unique symbol = Symbol();
		this.loaderSvc.addLoader(
			loader,
			'pages/leader/dashboard/track:loadAssessments',
		);
		this.assessments = this.allAssessments.filter(
			(assessment) => assessment.groupUUID === this.filters.group.value.uuid,
		);

		let activeAssessments = this.assessments.filter(
			(assessment) => assessment.status != 'SCHEDULED',
		);

		if (activeAssessments.length > 0) {
			const memory = activeAssessments.reduce((a, b) =>
				a.startDate > b.startDate ? a : b,
			);

			const date = new Date();

			let day = date.getDate();
			let month = date.getMonth() + 1;
			let year = date.getFullYear();

			// This arrangement can be altered based on how we want the date's format to appear.
			let currentDate = `${month}/${day}/${year}`;

			this.finalDate = Math.round(
				this.diffDates(currentDate, memory.startDate),
			);
		} else {
			this.finalDate = 0;
		}

		this.assessmentsCompleted = this.assessments.filter(
			(x) => x.status === 'COMPLETED',
		);
		this.assessmentsActive = this.assessments.filter(
			(x) => x.status === 'ACTIVE',
		);
		this.assessmentsPending = this.assessments.filter(
			(x) => x.status !== 'COMPLETED' && x.status !== 'ACTIVE',
		);
		this.loaderSvc.removeLoader(loader);
	}

	sortNumberColumn(type) {
		this.table_header_highlight = type;
		if (type == 'startdate') {
			this.endDate = false;
			this.status = false;
			this.ascNumberSort = !this.ascNumberSort;
			if (this.ascNumberSort) {
				this.assessments.sort((a, b) => (a.startDate > b.startDate ? 1 : -1));
			} else {
				this.assessments.sort((a, b) => (b.startDate > a.startDate ? 1 : -1));
			}
		} else if (type == 'enddate') {
			this.endDate = !this.endDate;
			this.ascNumberSort = true;
			this.status = false;
			if (this.endDate) {
				this.assessments.sort((a, b) => (a.endDate > b.endDate ? 1 : -1));
			} else {
				this.assessments.sort((a, b) => (b.endDate > a.endDate ? 1 : -1));
			}
		} else {
			this.ascNumberSort = true;
			this.endDate = false;
			this.status = !this.status;
			if (this.status) {
				this.assessments.sort((a, b) => (a.status > b.status ? 1 : -1));
			} else {
				this.assessments.sort((a, b) => (b.status > a.status ? 1 : -1));
			}
		}
	}

	createAssessment(): void {
		this.modalSvc.show(LeaderAssessmentCreateComponent, {
			class: 'modal-dialog-centered',
		});
	}

	viewAssessment(assessment: Assessment): void {
		if (
			assessment.status === AssessmentStatus.INACTIVE ||
			assessment.status === AssessmentStatus.SCHEDULED
		) {
			localStorage.setItem(
				'createAssessment',
				JSON.stringify({ formValue: assessment, from: 'view' }),
			);
			this.router.navigate(['/leader/assessment/skills']);
		} else this.router.navigate(['/leader/assessment/view', assessment.uuid]);
	}

	openModal(template: TemplateRef<any>, assessment: any) {
		this.deleteAss = assessment;
		this.modalRef = this.modalSvc.show(template, {
			class: 'modal-sm modal-dialog-centered',
		});
	}

	deleteAssessment(assessment): void {
		this.assessmentSvc
			.deleteAssessment(assessment.uuid)
			.subscribe(() => this.loadAllAssessments());
		this.modalRef?.hide();
	}

	decline(): void {
		this.modalRef?.hide();
	}

	ngOnDestroy(): void {
		this._subscriptions.unsubscribe();
	}
}
